import React, { useEffect, useState, useCallback, useRef, useTransition } from "react";
import {
  Grid,
  TextField,
  Box,
  Autocomplete,
  IconButton,
  Button,
  Paper,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";

import { useSelector } from "react-redux";
import {
  getAllCategories,
  getAllInputs,
  getAllRolls,
  getAllSupplier,
  getAllTelas,
} from "../functions";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { Add, Remove } from "@mui/icons-material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DescriptionIcon from "@mui/icons-material/Description";
import ScaleIcon from "@mui/icons-material/Scale";
import LayersIcon from "@mui/icons-material/Layers";
import BuildIcon from "@mui/icons-material/Build";

// Componente memoizado: solo se rerenderiza si sus propios datos cambian
const RollItem = React.memo(
  ({ index, cloth, code, color, totalKg, meters, telasData, setFieldValue, setValues }) => {
    const handleRemove = useCallback(() => {
      setValues((prev) => ({
        ...prev,
        amount: Math.max(0, prev.rolls.length - 1),
        rolls: prev.rolls.filter((_, i) => i !== index),
      }));
    }, [index, setValues]);

    return (
      <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: "1px solid rgba(244,63,94,0.12)", background: "rgba(253,242,244,0.6)", ".dark &": { background: "rgba(244,63,94,0.05)", borderColor: "rgba(244,63,94,0.2)" } }}>
        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.75rem", fontWeight: 600, color: "#be185d", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.5, display: "block" }}>
          Rollo #{index + 1}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              id={`rolls[${index}].cloth`}
              size="small"
              getOptionLabel={(option) => `${option.name}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => setFieldValue(`rolls[${index}].cloth`, value)}
              disablePortal
              value={cloth}
              options={telasData || []}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>{option.name}</li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Tela" color="primary" />
              )}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              id={`rolls[${index}].code`}
              label="Código"
              size="small"
              color="primary"
              value={code}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              id={`rolls[${index}].color`}
              label="Color"
              size="small"
              color="primary"
              value={color}
              onChange={(e) => setFieldValue(`rolls[${index}].color`, e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              id={`rolls[${index}].totalKg`}
              label="Total Kg"
              size="small"
              type="number"
              color="primary"
              value={totalKg}
              onChange={(e) => setFieldValue(`rolls[${index}].totalKg`, e.target.value)}
              inputProps={{ min: 0, step: "any" }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              id={`rolls[${index}].meters`}
              label="Metros"
              size="small"
              type="number"
              color="primary"
              value={meters}
              onChange={(e) => setFieldValue(`rolls[${index}].meters`, e.target.value)}
              inputProps={{ min: 0, step: "any" }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <IconButton color="error" onClick={handleRemove}>
              <Remove />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
    );
  },
  (prev, next) =>
    prev.index === next.index &&
    prev.cloth?.id === next.cloth?.id &&
    prev.code === next.code &&
    prev.color === next.color &&
    prev.totalKg === next.totalKg &&
    prev.meters === next.meters &&
    prev.telasData === next.telasData
);

const InputsFormFields = ({
  formik,
  setFieldValue,
  setIsMenuForm,
  isMenuForm,
}) => {
  const { defaultData } = useSelector(({ telas }) => telas);
  const [categories, setCategoryData] = useState(null);
  const [machinary, setMachinaryData] = useState(null);
  const [telasData, setTelasData] = useState([]);
  const [supplierData, setSupplierData] = useState(null);
  const [inputsData, setInputsData] = useState([]);
  const [allRollsData, setAllRollsData] = useState([]);
  const [isLoadingRolls, setIsLoadingRolls] = useState(false);
  const [isPendingRolls, startTransitionRolls] = useTransition();
  const rollTimerRef = useRef(null);
  // Ref siempre actualizada con los últimos valores de formik (sin closure rancia)
  const latestFormikRef = useRef(formik.values);
  latestFormikRef.current = formik.values;

  useEffect(() => {
    const fetchDataCategories = async () => {
      let categories = await getAllCategories();
      const dataCategories = categories?.filter(
        (item) => item?.type?.name === "Inventario"
      );
      const dataMachinaryData = categories?.filter(
        (item) => item?.type?.name === "Machinary"
      );
      setMachinaryData(dataMachinaryData);
      setCategoryData(dataCategories);
    };
    const fetchDataTelas = async () => {
      let telas = await getAllTelas();
      setTelasData(telas);
    };
    const fetchSupplier = async () => {
      let supplier = await getAllSupplier();
      setSupplierData(supplier);
    };
    const fetchDataInputs = async () => {
      let inputs = await getAllInputs();
      setInputsData(inputs);
    };
    const fetchAllRolls = async () => {
      let rolls = await getAllRolls();
      setAllRollsData(rolls || []);
    };
    fetchDataTelas();
    fetchDataCategories();
    fetchSupplier();
    fetchDataInputs();
    fetchAllRolls();
  }, []);

  function getNextCodeFixedInputs(data) {
    const prefix = "ENTRADA";
    const digits = 9; 

    const codes = data
      .map((item) => item.code)
      .filter((code) => code.startsWith(prefix));

    if (codes.length === 0) {
      return prefix + "1".padStart(digits, "0");
    }

    const lastNum = Math.max(
      ...codes.map((code) => parseInt(code.slice(prefix.length), 10))
    );

    const nextNum = lastNum + 1;
    const nextNumStr = nextNum.toString().padStart(digits, "0");

    return prefix + nextNumStr;
  }

  useEffect(() => {
    if (inputsData.length > 0 && !formik.values.code) {
      const nextCode = getNextCodeFixedInputs(inputsData);
      formik.setFieldValue("code", nextCode);
    }
  }, [inputsData, formik.values.code]);

  const filterSupplier = supplierData?.filter((item) => {
    if (isMenuForm !== null) {
      switch (isMenuForm.name) {
        case "Telas":
          return item.category.name === "Telas";
        default:
          null;
      }
    }
  });

  function getNextRollCode(dbRolls, formRolls) {
    const prefix = "TELA";
    const allCodes = [
      ...dbRolls.map((r) => r.code),
      ...formRolls.map((r) => r.code),
    ].filter((code) => typeof code === "string" && code.startsWith(prefix));

    if (allCodes.length === 0) return prefix + "000001";

    const lastNum = Math.max(
      ...allCodes.map((code) => parseInt(code.slice(prefix.length), 10) || 0)
    );
    const nextNum = lastNum + 1;
    let nextStr = nextNum.toString().padStart(6, "0");
    if (nextStr.length > 6) nextStr = nextStr.slice(nextStr.length - 6);
    return prefix + nextStr;
  }

  const handleAmountChange = (e) => {
    const newAmount = Math.max(0, parseInt(e.target.value) || 0);
    // Actualizar el campo de cantidad de inmediato (UI responsiva)
    formik.setFieldValue("amount", newAmount);

    // Debounce: cancelar timer anterior y arrancar uno nuevo
    if (rollTimerRef.current) clearTimeout(rollTimerRef.current);
    setIsLoadingRolls(true);

    rollTimerRef.current = setTimeout(() => {
      const values = latestFormikRef.current; // valores más recientes (sin closure rancia)
      const currentRolls = values.rolls;
      const finalAmount = values.amount;

      if (finalAmount === currentRolls.length) {
        setIsLoadingRolls(false);
        return;
      }

      let newRolls;
      if (finalAmount > currentRolls.length) {
        const toAdd = finalAmount - currentRolls.length;
        const prefix = "TELA";
        const allCodes = [
          ...allRollsData.map((r) => r.code),
          ...currentRolls.map((r) => r.code),
        ].filter((code) => typeof code === "string" && code.startsWith(prefix));
        const lastNum = allCodes.reduce(
          (max, code) => Math.max(max, parseInt(code.slice(prefix.length), 10) || 0),
          0
        );
        newRolls = [...currentRolls];
        for (let i = 0; i < toAdd; i++) {
          const code = prefix + String(lastNum + i + 1).padStart(6, "0").slice(-6);
          newRolls.push({ cloth: null, code, color: "", totalKg: "", meters: "" });
        }
      } else {
        newRolls = currentRolls.slice(0, finalAmount);
      }

      setIsLoadingRolls(false);
      startTransitionRolls(() => {
        formik.setValues({ ...values, rolls: newRolls });
      });
    }, 600);
  };

  return (
    <Box sx={{
      p: 3,
      background: "#fafafa",
      ".dark &": { background: "#0f172a" },
      "& .MuiOutlinedInput-root": {
        fontFamily: "'Poppins', sans-serif",
        fontSize: "0.9rem",
        borderRadius: 2,
        "&.Mui-focused fieldset": { borderColor: "#f43f5e" },
      },
      "& .MuiInputLabel-root": { fontFamily: "'Poppins', sans-serif" },
      "& .MuiInputLabel-root.Mui-focused": { color: "#f43f5e" },
    }}>
      {/* === Información General === */}
      <Box sx={{ p: 3, mb: 2, borderRadius: 2, border: "1px solid rgba(244,63,94,0.15)", background: "#fff", ".dark &": { background: "#1e293b", borderColor: "rgba(244,63,94,0.2)" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
          <AssignmentIcon sx={{ color: "#f43f5e", fontSize: 20 }} />
          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.07em", color: "#be185d" }}>
            Información General
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Autocomplete
              id="category"
              name="category"
              size="small"
              getOptionLabel={(option) => `${option.name}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => {
                setIsMenuForm(value);
                setFieldValue("category", value);
              }}
              disablePortal
              value={formik.values.category}
              options={categories || []}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>{option.name}</li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Categoría" color="primary" />
              )}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es-mx">
              <DatePicker
                name="date"
                label="Fecha de Entrada"
                value={moment(formik.values.date)}
                closeOnSelect
                sx={{ width: "100%" }}
                slotProps={{ textField: { size: "small", variant: "outlined" } }}
                showDaysOutsideCurrentMonth
                disableHighlightToday
                onChange={(value) => setFieldValue("date", value)}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              id="code"
              name="code"
              size="small"
              label="Código"
              value={formik.values.code}
              onChange={formik.handleChange}
              color="primary"
              disabled
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              id="amount"
              name="amount"
              size="small"
              type="number"
              label={
                isMenuForm?.name === "Telas"
                  ? "Cantidad de rollos"
                  : isMenuForm?.name === "Muebles"
                  ? "Cantidad de Muebles"
                  : "Cantidad de Maquinaria"
              }
              value={formik.values.amount}
              onChange={isMenuForm?.name === "Telas" ? handleAmountChange : formik.handleChange}
              color="primary"
              inputProps={{ min: 0 }}
              error={formik.touched.amount && Boolean(formik.errors.amount)}
              helperText={formik.touched.amount && formik.errors.amount}
            />
          </Grid>
        </Grid>
      </Box>

      {/* === Detalles === */}
      <Box sx={{ p: 3, mb: 2, borderRadius: 2, border: "1px solid rgba(244,63,94,0.15)", background: "#fff", ".dark &": { background: "#1e293b", borderColor: "rgba(244,63,94,0.2)" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
          <DescriptionIcon sx={{ color: "#f43f5e", fontSize: 20 }} />
          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.07em", color: "#be185d" }}>
            Detalles
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="description"
              name="description"
              size="small"
              label="Descripción de la Entrada"
              multiline
              rows={3}
              value={formik.values.description}
              onChange={formik.handleChange}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              id="supplier"
              name="supplier"
              size="small"
              getOptionLabel={(option) =>
                `${option.persona.nombre} ${option.persona.apellidoPaterno} ${option.persona.apellidoMaterno}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => setFieldValue("supplier", value)}
              disablePortal
              value={formik.values.supplier}
              options={filterSupplier || []}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {`${option.persona.nombre} ${option.persona.apellidoPaterno} ${option.persona.apellidoMaterno}`}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Proveedor" color="primary" />
              )}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              id="unit"
              name="unit"
              size="small"
              getOptionLabel={(option) => `${option.name}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => setFieldValue("unit", value)}
              disablePortal
              value={formik.values.unit}
              options={[
                { id: 1, name: "Pieza" },
                { id: 2, name: "Caja" },
                { id: 3, name: "Bolsa" },
                { id: 4, name: "Rollo" },
              ]}
              renderInput={(params) => (
                <TextField {...params} label="Tipo de Unidad" color="primary" />
              )}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>
      {/* === Telas: Totales y Rollos === */}
      {isMenuForm?.name === "Telas" && (
        <Box sx={{ p: 3, mb: 2, borderRadius: 2, border: "1px solid rgba(244,63,94,0.15)", background: "#fff", ".dark &": { background: "#1e293b", borderColor: "rgba(244,63,94,0.2)" } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
            <ScaleIcon sx={{ color: "#f43f5e", fontSize: 20 }} />
            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.07em", color: "#be185d" }}>
              Totales de la Entrada
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                id="kg"
                name="kg"
                label="Total de Kg (auto)"
                size="small"
                type="number"
                color="primary"
                value={formik.values.kg}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputProps={{ min: 0, step: "any" }}
                disabled
                error={formik.touched.kg && Boolean(formik.errors.kg)}
                helperText={formik.touched.kg && formik.errors.kg}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                id="meters"
                name="meters"
                size="small"
                type="number"
                label="Total de Metros (auto)"
                value={formik.values.meters}
                onChange={formik.handleChange}
                color="primary"
                inputProps={{ min: 0, step: "any" }}
                disabled
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <LayersIcon sx={{ color: "#f43f5e", fontSize: 18 }} />
            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.07em", color: "#be185d" }}>
              Rollos ({formik.values.rolls?.length || 0})
            </Typography>
          </Box>
          {(isLoadingRolls || isPendingRolls) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, px: 1.5, py: 1, borderRadius: 2, background: "rgba(244,63,94,0.06)" }}>
              <CircularProgress size={18} sx={{ color: "#f43f5e" }} />
              <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.8rem", color: "#be185d", fontWeight: 500 }}>
                Generando rollos...
              </Typography>
            </Box>
          )}
          <div style={{ width: "100%" }}>
            {formik.values.rolls.map((roll, index) => (
              <RollItem
                key={index}
                index={index}
                cloth={roll.cloth}
                code={roll.code}
                color={roll.color}
                totalKg={roll.totalKg}
                meters={roll.meters}
                telasData={telasData}
                setFieldValue={setFieldValue}
                setValues={formik.setValues}
              />
            ))}
            <Button
              variant="outlined"
              size="small"
              sx={{
                fontFamily: "'Poppins', sans-serif", fontWeight: 600, borderRadius: 2,
                borderColor: "#f43f5e", color: "#f43f5e", mt: 1,
                "&:hover": { borderColor: "#e11d48", background: "#fff0f6" },
              }}
              onClick={() => {
                const code = getNextRollCode(allRollsData, formik.values.rolls);
                formik.setValues((prev) => ({
                  ...prev,
                  amount: prev.rolls.length + 1,
                  rolls: [...prev.rolls, { cloth: null, code, color: "", totalKg: "", meters: "" }],
                }));
              }}
              startIcon={<Add />}
            >
              Agregar Rollo
            </Button>
          </div>
        </Box>
      )}

      {/* === Maquinaria === */}
      {isMenuForm?.name === "Maquinaria" && (
        <Box sx={{ p: 3, mb: 2, borderRadius: 2, border: "1px solid rgba(244,63,94,0.15)", background: "#fff", ".dark &": { background: "#1e293b", borderColor: "rgba(244,63,94,0.2)" } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
            <BuildIcon sx={{ color: "#f43f5e", fontSize: 20 }} />
            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.07em", color: "#be185d" }}>
              Maquinaria
            </Typography>
          </Box>
          <FieldArray
            name="machinary"
            render={(arrayHelpers) => (
              <div>
                {formik.values.machinary.map((item, index) => (
                  <Box key={index} sx={{ p: 2, mb: 2, borderRadius: 2, border: "1px solid rgba(244,63,94,0.12)", background: "rgba(253,242,244,0.6)", ".dark &": { background: "rgba(244,63,94,0.05)", borderColor: "rgba(244,63,94,0.2)" } }}>
                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.75rem", fontWeight: 600, color: "#be185d", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.5, display: "block" }}>
                      Maquinaria #{index + 1}
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <Autocomplete
                          id={`machinary[${index}].category`}
                          name={`machinary[${index}].category`}
                          size="small"
                          getOptionLabel={(option) => `${option.name}`}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          onChange={(e, value) => setFieldValue(`machinary[${index}].category`, value)}
                          disablePortal
                          value={formik.values.machinary[index].category}
                          options={machinary || []}
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>{option.name}</li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Tipo de Maquinaria"
                              color="primary"
                              error={formik.touched.machinary?.[index] && Boolean(formik.errors.machinary?.[index]?.category)}
                              helperText={formik.touched.machinary?.[index] && formik.errors.machinary?.[index]?.category}
                            />
                          )}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          id={`machinary[${index}].name`}
                          name={`machinary[${index}].name`}
                          label="Nombre"
                          size="small"
                          color="primary"
                          value={formik.values.machinary[index].name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.machinary?.[index] && Boolean(formik.errors.machinary?.[index]?.name)}
                          helperText={formik.touched.machinary?.[index] && formik.errors.machinary?.[index]?.name}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          id={`machinary[${index}].code`}
                          name={`machinary[${index}].code`}
                          label="Código"
                          size="small"
                          color="primary"
                          value={formik.values.machinary[index].code}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.machinary?.[index] && Boolean(formik.errors.machinary?.[index]?.code)}
                          helperText={formik.touched.machinary?.[index] && formik.errors.machinary?.[index]?.code}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          id={`machinary[${index}].responsible`}
                          name={`machinary[${index}].responsible`}
                          label="Responsable"
                          size="small"
                          color="primary"
                          value={formik.values.machinary[index].responsible}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.machinary?.[index] && Boolean(formik.errors.machinary?.[index]?.responsible)}
                          helperText={formik.touched.machinary?.[index] && formik.errors.machinary?.[index]?.responsible}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es-mx">
                          <DatePicker
                            label="Fecha de Mantenimiento"
                            value={moment(formik.values.machinary[index].dateMaintenance)}
                            closeOnSelect
                            sx={{ width: "100%" }}
                            slotProps={{ textField: { size: "small", variant: "outlined" } }}
                            showDaysOutsideCurrentMonth
                            disableHighlightToday
                            onChange={(value) => setFieldValue(`machinary[${index}].dateMaintenance`, value)}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          id={`machinary[${index}].description`}
                          name={`machinary[${index}].description`}
                          label="Descripción"
                          size="small"
                          color="primary"
                          multiline
                          rows={2}
                          value={formik.values.machinary[index].description}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.machinary?.[index] && Boolean(formik.errors.machinary?.[index]?.description)}
                          helperText={formik.touched.machinary?.[index] && formik.errors.machinary?.[index]?.description}
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <IconButton
                          color="error"
                          onClick={() => arrayHelpers.remove(index)}
                        >
                          <Remove />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    fontFamily: "'Poppins', sans-serif", fontWeight: 600, borderRadius: 2,
                    borderColor: "#f43f5e", color: "#f43f5e", mt: 1,
                    "&:hover": { borderColor: "#e11d48", background: "#fff0f6" },
                  }}
                  onClick={() => arrayHelpers.push({ id: 0, code: "", is_void: false })}
                  startIcon={<Add />}
                >
                  Agregar Maquinaria
                </Button>
              </div>
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export default InputsFormFields;
